import React from 'react';
import { Link } from 'react-router-dom';
import styled from 'styled-components';
import { useWorks } from '../contexts/WorksContext';
import { createSlug } from './WorkDetail';

const CommissionsContainer = styled.div`
  max-width: 800px;
  margin: 0 auto;
  height: 100%;
  display: flex;
  flex-direction: column;
  gap: 30px;
  
  @media (min-width: 768px) {
    flex-direction: row;
  }
`;

const MainContent = styled.div`
  flex: 2.5;
`;

const Sidebar = styled.div`
  flex: 1;
`;

const CommissionExample = styled.div`
  margin-bottom: 30px;
`;

const CommissionTitle = styled.h4`
  margin: 0 0 6px 0;
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  font-size: 1.1rem;
`;

const CommissionLink = styled(Link)`
  color: #333;
  text-decoration: none;
  transition: color 0.2s;
  
  &:hover {
    color: #666;
    text-decoration: underline;
  }
`;

const CommissionInfo = styled.p`
  margin: 0;
  font-family: 'Inter', sans-serif;
  font-size: 0.8rem;
  color: #666;
`;

const PageTitle = styled.h1`
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  font-size: 1.65rem;
  margin: 0 0 15px 0;
  color: #333;
`;

const Paragraph = styled.p`
  font-family: 'Inter', sans-serif;
  font-size: 0.9rem;
  line-height: 1.6;
  margin-bottom: 20px;
  color: #444;
`;

const SectionTitle = styled.h3`
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  font-size: 1.3rem;
  margin: 30px 0 15px 0;
  color: #333;
`;

const SidebarTitle = styled.h3`
  font-family: 'Lucida Bright', 'Lucida Serif', Georgia, serif;
  font-size: 1.1rem;
  margin: 0 0 20px 0;
  color: #333;
`;

const Commissions: React.FC = () => {
  const { works } = useWorks();
  
  // Function to find a work by its title
  const findWorkByTitle = (title: string) => {
    return works.find(work => 
      work.title.toLowerCase() === title.toLowerCase()
    );
  };
  
  // List of commission examples
  const commissionExamples = [
    { title: "Ave Maria", commissioner: "Kantos Chamber Choir, Manchester" },
    { title: "Winter's Bastard", commissioner: "Toby Holden" },
    { title: "The Heart of Lancashire", commissioner: "Blackburn Music Society" },
    { title: "Kigo", commissioner: "Vizma Zvaigne" },
    { title: "4 Schiller Vierzeiler", commissioner: "the Schillerchor, Berlin" }
  ];
  
  return (
    <CommissionsContainer>
      <MainContent>
        <PageTitle>Commissions</PageTitle>
        
        <Paragraph>
          Commissions are a unique and exciting way to collaborate
          with a contemporary composer. As a composer, I welcome
          the opportunity to work with performers, ensembles, and
          organizations to create new music that is tailored to their
          unique vision.
        </Paragraph>
        
        <Paragraph>
          When you commission a piece of music, you have the
          opportunity to bring your creative ideas to life. Whether
          you're looking for a new work to premiere at a concert, a
          special event, or for your own personal collection, I'm
          excited to work with you to create a piece that perfectly
          captures your vision.
        </Paragraph>
        
        <Paragraph>
          Commissioning a piece of music can be a rewarding
          experience for both the commissioner and the composer.
          As a commissioner, you have the opportunity to support
          new music and to be a part of the creative process. As a
          composer, I am able to stretch my artistic boundaries and
          create a new work that is both challenging and fulfilling.
        </Paragraph>
        
        <Paragraph>
          If you're interested in commissioning a piece of music,
          please feel free to get in touch with me. I'm always excited
          to explore new creative ideas and to work with others to
          create something truly unique and special.
        </Paragraph>
        
        <SectionTitle>Rate and Timeline</SectionTitle>
        
        <Paragraph>
          When it comes to commissioning a new piece of music, the
          timeframe can vary depending on the length and complexity
          of the work. Generally, for a shorter piece that's around 5
          minutes or less, a timeframe of six months from signing the
          commissioning agreement to delivery is a good starting
          point. However, for longer works or pieces that involve
          chamber or large-ensemble instrumental arrangements, a
          longer timeframe may be more appropriate. It's worth noting
          that I'm often able to complete a piece earlier than the
          estimated timeframe, depending on my schedule and the time
          of year. If you have specific needs or deadlines in mind,
          don't hesitate to let me know and we can discuss what's
          possible.
        </Paragraph>

        <Paragraph>
          As for rates, I understand that commissioning new music can
          be a financial commitment, and I'm always open to discussing
          fees that work for everyone involved. While there may be some
          room for negotiation, I aim to find an agreement that's fair
          and feasible for both parties. If you have a specific budget
          or financial limit in mind, please let me know so that we can
          factor that into our discussion. In terms of payment, the
          standard practice is to pay 50% of the commission fee upon
          signing the agreement, and the remaining 50% upon delivery
          of the piece. However, I'm open to alternative payment
          structures, such as monthly payment plans, depending on your
          needs and situation.
        </Paragraph>

        <Paragraph>
          Please use the <Link to="/contact">contact page</Link> and we can arrange a time to
          discuss your ideas.
        </Paragraph>
      </MainContent>
      
      <Sidebar>
        <SidebarTitle>Previous Commission Examples:</SidebarTitle>
        
        {commissionExamples.map((commission, index) => {
          const work = findWorkByTitle(commission.title);
          
          return (
            <CommissionExample key={index}>
              {work ? (
                <CommissionTitle>
                  <CommissionLink to={`/works/${createSlug(commission.title)}`}>
                    {commission.title}
                  </CommissionLink>
                </CommissionTitle>
              ) : (
                <CommissionTitle>{commission.title}</CommissionTitle>
              )}
              <CommissionInfo>commissioned by {commission.commissioner}</CommissionInfo>
            </CommissionExample>
          );
        })}
      </Sidebar>
    </CommissionsContainer>
  );
};

export default Commissions; 